import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Button, Row, Col, InputGroup } from "react-bootstrap";
import { FiUpload } from "react-icons/fi";
import CourseService from "../../../services/CourseService";
import AuthContext from "../../../auth/AuthContext";
import SupportService from "../../../services/SupportService";
import AlertService from "../../../services/AlertService";
import { Dropdown } from "primereact/dropdown";
import { FaTrash } from "react-icons/fa";
import { Skeleton } from "primereact/skeleton";

const AddTicket = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const categories = [
    { value: 1, label: "Login" },
    { value: 2, label: "Technical" },
    { value: 3, label: "Learning Material" },
    { value: 4, label: "Quiz/Assessment" },
  ];
  const [courseId, setCourseId] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [files, setFiles] = useState([]);
  const [courseIsValid, setCourseIsValid] = useState(true);
  const [subjectIsValid, setSubjectIsValid] = useState(true);
  const [descriptionIsValid, setDescriptionIsValid] = useState(true);
  const [categoryIsValid, setCategoryIsValid] = useState(true);
  const fileInputRef = useRef(null);
  const authContext = useContext(AuthContext);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const submitControllerRef = useRef(null);

  const loadCourses = useCallback(async (signal) => { 
    try {
      const data = await CourseService.GetCoursesByUser(
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        if (data.data.length === 0) {
          AlertService.info('Non Course assigned to you. Unable to proceed!!');
          navigate("/support");
        }
        setCourses(data.data);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext, navigate]);

useEffect(() => {
    const controller = new AbortController(); 
    loadCourses(controller.signal); 
    return () => controller.abort(); 
  }, [loadCourses]);

  const onCancelHandler = () => {
    navigate("/support");
  };



const onSubmitHandler = async (event) => {
  event.preventDefault();
  console.log("Real Submit Clicked....");

  if (isSubmitted) return;

  let isValid = true;
  if (!courseId) {
    setCourseIsValid(false);
    isValid = false;
  }
  if (!subject.trim()) {
    setSubjectIsValid(false);
    isValid = false;
  }
  if (!description.trim()) {
    setDescriptionIsValid(false);
    isValid = false;
  }
  if (!categoryId) {
    setCategoryIsValid(false);
    isValid = false;
  }

  if (!isValid) return;
  setIsSubmitted(true);

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file.data);
    });

    const selectedCourse = courses.find((c) => c.value === courseId);

    formData.append("courseId", courseId);
    formData.append("courseName", selectedCourse?.label || "");
    formData.append("ticketNumber", 0);
    formData.append("subject", subject);
    formData.append("description", description);
    formData.append("categoryId", categoryId);
    formData.append("requesterId", authContext.userId);
    formData.append("assignedTo", 0);
    formData.append("status", 1);
    formData.append("isReopened", false);
    formData.append("isActive", true);
    formData.append("isDeleted", false);
    formData.append("attachments", []);

    const data = await SupportService.Save(
      formData,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (data.isSuccess) {
      navigate("/support");
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (ex) {
    if (ex.name === "AbortError") return;
    console.error("Error submitting support ticket:", ex);
    AlertService.error("An error occurred while submitting your request");
  } finally {
    if (!controller.signal.aborted) {
      setLoading(false);
      setIsSubmitted(false);
    }
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);

        const invalidFiles = newFiles.filter(file => file.type !== "application/pdf");
        if (invalidFiles.length > 0) {
            AlertService.error("Please select PDF files only. Other file types are not supported!");
            return;
        }

        setFiles(prevFiles => [
            ...prevFiles,
            ...newFiles.map(file => ({
                fileName: file.name,
                filePath: file.name,
                data: file
            }))
        ]);

        e.target.value = "";
    };

    const removeFileHandler = (fileName) => {
        setFiles(prevFiles => prevFiles.filter(file => file.fileName !== fileName));
    };

  return (
    <div className="container-fluid custom-container mt-3 pb-3">
      <Form className="ms-2 mt-4" onSubmit={onSubmitHandler}>
        {loading ? (
          <>
            <div className="mb-4">
              <Skeleton width="30%" height="2rem" className="mb-2" />
              <Skeleton width="100%" height="2.5rem" />
            </div>
            <div className="mb-4">
              <Skeleton width="30%" height="2rem" className="mb-2" />
              <Skeleton width="100%" height="2.5rem" />
            </div>
            <div className="mb-4">
              <Skeleton width="30%" height="2rem" className="mb-2" />
              <Skeleton width="100%" height="2.5rem" />
            </div>
            <div className="mb-4">
              <Skeleton width="30%" height="2rem" className="mb-2" />
              <Skeleton width="100%" height="10rem" />
            </div>
            <div className="mb-4">
              <Skeleton width="30%" height="2rem" className="mb-2" />
              <Skeleton width="100%" height="2.5rem" />
            </div>
          </>
        ) : (
          <>
            <Form.Group as={Row} className="mb-4">
              <Form.Label column md={2} className="d-flex align-items-center">
                Category
              </Form.Label>
              <Col>
                <Dropdown
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(e.value);
                    setCategoryIsValid(true);
                  }}
                  options={categories}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select a Category"
                  filter
                  className={`w-100 custom-input custom-input-color select-dropdown ${!categoryIsValid ? "p-invalid" : ""}`}
                                          panelClassName="select-dropdown-panel"
      emptyMessage="No category found"
      emptyFilterMessage="No matching category found"
                  disabled={loading}
                />
                {!categoryIsValid && (
                  <div className="invalid-feedback d-block">
                    Please select a category
                  </div>
                )}
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-4">
              <Form.Label column md={2} className="d-flex align-items-center">
                Course
              </Form.Label>
              <Col>
                <Dropdown
                  value={courseId}
                  onChange={(e) => {
                    setCourseId(e.value);
                    setCourseIsValid(true);
                  }}
                  options={courses}
                  filter
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select a Course"
                  className={`w-100 custom-input custom-input-color select-dropdown ${!courseIsValid ? "p-invalid" : ""}`}
                                          panelClassName="select-dropdown-panel"
      emptyMessage="No courses found"
      emptyFilterMessage="No matching course found"
                  disabled={loading}
                />
                       {!courseIsValid && (
                                    <div className="invalid-feedback d-block">Please select a course</div>
                                )}
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-4">
              <Form.Label column md={2} className="d-flex align-items-center">
                Subject
              </Form.Label>
              <Col>
                <Form.Control
                  className={`custom-input custom-input-color ${!subjectIsValid ? "is-invalid" : ""}`}
                  type="text"
                  placeholder="Subject"
                  maxLength="150"
                  value={subject}
                  onChange={(e) => {
                    setSubject(e.target.value);
                    setSubjectIsValid(true);
                  }}
                  disabled={loading}
                />
       {!subjectIsValid && <div className="invalid-feedback">Please provide a subject</div>}
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-4">
              <Form.Label column md={2} className="d-flex align-items-center">
                Description
              </Form.Label>
              <Col>
                <Form.Control
                  className={` custom-input-color ${!descriptionIsValid ? "is-invalid" : ""}`}
                  as="textarea"
                  rows={10}
                  placeholder="Description"
                  maxLength="800"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setDescriptionIsValid(true);
                  }}
                  disabled={loading}
                />
         {!descriptionIsValid && <div className="invalid-feedback">Please provide a description</div>}
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-0">
              <Form.Label
                column
                md={2}
                className="d-flex align-items-center"
                style={{ minHeight: "48px" }}
              >
                Files Upload
              </Form.Label>
              <Col>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder={
                      files.length > 0
                        ? `${files.length} file(s) selected`
                        : "Upload file"
                    }
                    readOnly
                    className="py-3 custom-input-color custom-input"
                    style={{ background: "#fff" }}
                  />
                  <Button
                    variant="primary"
                    className="px-3 rounded  rounded-start-0"
                    onClick={() => fileInputRef.current.click()}
                    disabled={loading}
                    type="button"
                  >
                    <FiUpload className="fs-4" />
                  </Button>
                  <Form.Control
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="d-none"
                    multiple
                    accept="application/pdf"
                  />
                </InputGroup>
              </Col>
            </Form.Group>

            {files.length > 0 && (
              <Row>
                <Col md={{ span: 10, offset: 2 }}>
                  <div>
                    {files.map((file, index) => (
                      <div key={index} className="d-flex align-items-center">
                        <button
                          className="btn text-danger mb-1 border-0"
                          onClick={() => removeFileHandler(file.fileName)}
                          disabled={loading}
                          type="button"
                        >
                          <FaTrash />
                        </button>
                        <span>{file.fileName}</span>
                      </div>
                    ))}
                  </div>
                </Col>
              </Row>
            )}
          </>
        )}
        <div className="d-flex justify-content-center justify-content-md-end mt-5">
          <Button
            variant="outline"
            className="px-lg-5 w-auto py-2 me-3"
            onClick={onCancelHandler}
            disabled={loading}
          >
            Cancel
          </Button>

          {!isSubmitted && (
            <Button
              variant="primary"
              className="px-lg-5 w-auto py-2 d-flex align-items-center justify-content-center"
              type="submit"
              disabled={loading}
            >
              Submit
            </Button>
          )}

          {isSubmitted && (
            <Button
              variant="primary"
              className="px-lg-5 w-auto py-2 d-flex align-items-center justify-content-center"
              type="button"
              disabled
            >
              Submit
            </Button>
          )}
        </div>
      </Form>
    </div>
  );
};

export default AddTicket;
